/**
 * JSON Schema generation for Mycelium events
 *
 * Produces machine-readable schema for both TypeScript and Go consumers.
 * Used by build step to generate mycelium-event.schema.json.
 *
 * @module schemas
 * @since 2026-05-05
 */

import { z } from 'zod'
import { zodToJsonSchema } from 'zod-to-json-schema'
import {
  MyceliumEventPriority,
  MyceliumWorkerEvent,
  MYCELIUM_SCHEMA_VERSION,
} from './types/events.js'
import { BackboneEventSchema, EventDomainSchema, EventMetadataSchema } from './types/backbone.js'

/**
 * Master schema envelope for all Mycelium events.
 * Includes both wire shape (MyceliumWorkerEvent) and in-app shape (BackboneEvent).
 */
export const MyceliumSchemaRegistry = z.object({
  version: z.literal(MYCELIUM_SCHEMA_VERSION),
  timestamp: z.string().datetime().describe('Schema lock timestamp'),
  description: z.string().default('Mycelium Event Schema v1'),
  schemas: z.object({
    MyceliumWorkerEvent: z.any(),
    BackboneEvent: z.any(),
    EventDomain: z.any(),
    EventPriority: z.any(),
    EventMetadata: z.any(),
  }),
})

/**
 * Generate JSON Schema for the master registry (all Mycelium event types)
 *
 * This is the canonical source for both TypeScript and Go consumers.
 * Must be kept in sync across both runtimes via contract tests.
 *
 * @returns Raw JSON Schema object (not stringified)
 */
export function generateMyceliumSchema() {
  return {
    version: MYCELIUM_SCHEMA_VERSION,
    timestamp: new Date().toISOString(),
    description: 'Mycelium Event Schema v1 - Locked 2026-05-05',
    schemas: {
      MyceliumWorkerEvent: zodToJsonSchema(MyceliumWorkerEvent, {
        name: 'MyceliumWorkerEvent',
      }),
      BackboneEvent: zodToJsonSchema(BackboneEventSchema, {
        name: 'BackboneEvent',
      }),
      EventDomain: zodToJsonSchema(EventDomainSchema, {
        name: 'EventDomain',
      }),
      EventPriority: zodToJsonSchema(MyceliumEventPriority, {
        name: 'EventPriority',
      }),
      EventMetadata: zodToJsonSchema(EventMetadataSchema, {
        name: 'EventMetadata',
      }),
    },
  }
}

/**
 * Export schema as JSON string for file output.
 */
export function serializeMyceliumSchema(): string {
  return JSON.stringify(generateMyceliumSchema(), null, 2)
}
